import React from "react";
import styled, { css } from "styled-components";
import { DOTS, usePagination } from "../hooks/usePagination";
import { useDevice } from "../hooks/useDevice";
import CustomIcon from "./image/CustomIcon";
import { SizedBox } from "./CommonBaseComponents";

const PaginationContainer = styled.nav`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
`;

const PageItem = styled.button`
    display: flex;
    justify-content: center;
    align-items: center;
    min-width: 28px;
    height: 28px;
    padding: 0 8px;
    border-radius: 8px;
    border: none;
    background-color: #f6f6f6;
    color: #374151;
    font-size: 11px;
    font-weight: 400;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background-color: #e5e7eb;
    }

    ${({ $active }) =>
        $active &&
        css`
            background-color: #6250fc;
            color: #f6f6f6;
            font-weight: 500;

            &:hover {
                background-color: #4f46e5;
            }
        `}
`;

const Ellipsis = styled.span`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 28px;
    height: 28px;
    color: #6b7280;
`;

const Pagination = ({ currentPage, totalPages, onPageChange, siblingCount = 1 }) => {
    const currentDevice = useDevice();

    const blockSize = currentDevice === "mobile" ? 5 : 10;
    const paginationRange = usePagination({
        currentPage,
        totalPages,
        siblingCount,
        blockSize,
    });

    // 페이지가 0이거나 없는 경우 렌더링 X
    if (!currentPage || paginationRange.length < 2) {
        return null;
    }

    const onNext = () => {
        if (currentPage >= totalPages) return;
        onPageChange(Number(currentPage) + 1);
    };

    const onPrevious = () => {
        if (currentPage <= 1) return;
        onPageChange(Number(currentPage) - 1);
    };

    return (
        <PaginationContainer aria-label="Pagination">
            <SizedBox
                height={"24px"}
                width={"24px"}
                cursor={currentPage === 1 ? "default" : "pointer"}
                onClick={onPrevious}
            >
                <CustomIcon
                    iconName={"page_prev"}
                    size={"16px"}
                    isDisabled={currentPage === 1}
                    cursor={currentPage === 1 ? "default" : "pointer"}
                />
            </SizedBox>
            {paginationRange.map((pageNumber, index) => {
                if (pageNumber === DOTS) {
                    return <Ellipsis key={`dots-${index}`}>...</Ellipsis>;
                }

                return (
                    <PageItem
                        key={pageNumber}
                        $active={pageNumber == currentPage}
                        onClick={() => onPageChange(pageNumber)}
                    >
                        {pageNumber}
                    </PageItem>
                );
            })}
            <SizedBox
                height={"24px"}
                width={"24px"}
                cursor={currentPage == totalPages ? "default" : "pointer"}
                onClick={onNext}
            >
                <CustomIcon
                    iconName={"page_next"}
                    size={"16px"}
                    isDisabled={currentPage == totalPages}
                    cursor={currentPage === 1 ? "default" : "pointer"}
                />
            </SizedBox>
        </PaginationContainer>
    );
};

export default Pagination;
